import {observer} from 'mobx-react';
import {chain, transform, map} from 'lodash';
import {useEffect} from 'react';

import {Positioner} from '../Positioner';
import {eptWidth, eptHeight} from '../settings';
import Ept from './Ept';
import Link from './Link';
import ApplicationPoint from './ApplicationPoint';

// const eptOrder = ([, ept1], [, ept2]) => ept1.order < ept2.order ? -1 : 1;

const getPosition = (id, epts, isInput) => {
  const ept = epts[id];

  if (!ept) {
    // Error case: user data contains reference to the primitive
    // that is not in the sausage
    return {x: 0, y: 0};
  } else if (!id) {
    // Application Point - return position as is
    return ept.position;
  }

  return {x: ept.position.x + eptWidth / 2, y: ept.position.y + (isInput ? 0 : eptHeight)};
};

const Visualizer = ({ept, readonly, showTooltip, comparisonEpt, isComparisonDirect}) => {
  const {epts = {}, links = {}} = ept ?? {};

  const compares = comparisonEpt?.epts || {};

  useEffect(() => {
    // Handling case when ept doesn't have positions information
    // (auto-generated templates). Invoke stacking logic in that case
    const positioner = new Positioner(epts, links);

    const eptConnections = transform(links, (result, link) => {
      result[link.to] = epts[link.from];
      return result;
    }, {});

    // Calculate placement position for the primitives without one specified
    chain(epts)
      .filter('isNotPositioned')
      .forEach((eptItem) => positioner.position(eptItem, eptConnections[eptItem.id]))
      .value();
  }, [epts, links]);

  return [
    ...map(links, (link, id) =>
      <Link
        key={id}
        id={id}
        from={getPosition(link.from, epts, false)}
        to={getPosition(link.to, epts, true)}
        disabled={readonly}
        activeEpt={ept}
      />
    ),
    ...chain(epts)
      .sortBy('order')
      .map((singleEpt) =>
        singleEpt.id ?
          <Ept
            key={singleEpt.id}
            activeEpt={ept}
            ept={singleEpt}
            disabled={readonly}
            showTooltip={showTooltip}
            compareWith={compares[singleEpt.id]}
            isComparisonDirect={isComparisonDirect}
          /> :
          <ApplicationPoint
            key='ap'
            data={singleEpt}
            disabled={readonly}
            activeEpt={ept}
          />
      )
      .value(),
  ];
};

export default observer(Visualizer);
